import { ChangeDetectorRef, Directive, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, ValidationErrors } from '@angular/forms';
import { DfEventService } from '@df/core/service/df-event';
import { TimeoutService } from '@df/ng/timeout.service';
import { NDfModuleLoqate } from '../df-module-loqate.config';
import { LoqateEmailValidationEvent, LoqateEmailValidatorService } from './loqate-email-validator.service';

declare module '@df/core/service/df-event' {
    interface IEventType {
        [NDfModuleLoqate.Events.EmailValidation]: DfEventConfig<
            NDfModuleLoqate.Events.EmailValidation,
            {
                value: string | undefined | null;
            },
            void
        >;
    }
}

/**
 * A directive that adds the `loqate` validator to controls marked with the
 * `email` attribute, via the `NG_ASYNC_VALIDATORS` provider.
 *
 * @example
 * ```html
 * <input type="text" loqateEmail [(ngModel)]="email" />
 * ```
 */
@Directive({
    selector: '[loqateEmail][ngModel],[loqateEmail][formControl],[loqateEmail][formControlName]',
    standalone: true,
    providers: [
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: LoqateEmailValidateDirective,
            multi: true
        }
    ]
})
export class LoqateEmailValidateDirective implements AsyncValidator, OnChanges, OnDestroy {
    @Input()
    loqateEmail = true;

    @Input()
    loqateEmailDebounceTime = 0;

    @Output()
    loqateEmailResponse = new EventEmitter<LoqateEmailValidationEvent>();

    protected loqateEmailValidatorService = inject(LoqateEmailValidatorService);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected dfEventService = inject(DfEventService);
    protected timeoutService = inject(TimeoutService);

    private _onChange!: () => void;
    private _timeout?: number;

    /**
     * A flag that tracks whether this validator is enabled.
     */
    private _enabled?: boolean;

    ngOnChanges(changes: SimpleChanges) {
        if (changes['loqateEmail']) {
            const input = changes['loqateEmail'].currentValue;
            this._enabled = this.enabled(input);

            if (this._onChange) {
                this._onChange();
            }
        }
    }

    ngOnDestroy() {
        if (this._timeout) {
            clearTimeout(this._timeout);
            this._timeout = undefined;
        }
    }

    validate(control: AbstractControl): Promise<ValidationErrors | null> {
        if (!this._enabled) {
            return Promise.resolve(null);
        }

        if (this._timeout) {
            clearTimeout(this._timeout);
        }

        return new Promise(resolve => {
            this._timeout = this.timeoutService.setTimeout(async () => {
                const result = await this.loqateEmailValidatorService.validate(control, this.loqateEmailResponse);

                this.dfEventService.dispatch({
                    id: NDfModuleLoqate.Events.EmailValidation,
                    data: {
                        value: control.value
                    }
                });

                resolve(result);
            }, this.loqateEmailDebounceTime);
        });
    }

    /**
     * Registers a callback function to call when the validator inputs change.
     */
    registerOnValidatorChange(fn: () => void): void {
        this._onChange = fn;
    }

    enabled(input: boolean | string | null): boolean {
        if (typeof input === 'boolean') {
            return input;
        }

        if (typeof input === 'string') {
            return input !== 'false';
        }

        return true;
    }
}
